import { hasAddressLine2AsCustom } from "@/shared/utils";
import {
  PROPERTY_FORM_FIELDS,
  PropertyFormFieldConfig,
  addressFields,
  addressFieldsWithAddress2,
} from "@constants/propertyFields";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldButtonSize, EmeraldButtonVariant } from "@emerald/nxcore";
import { useSurvey } from "@hooks/useSurveySelectors";
import { DetailedSurvey } from "@survey-types/survey.core.types";
import { useState } from "react";
import AddOrEditCustomFieldInput from "./AddOrEditCustomFieldInput";
import PropertyDisplayFields from "./PropertyDisplayFields";

/**
 * Simulates 2-column grid placement and returns a map of field → "full" | "normal".
 * "full" = col-span-2 col-start-1 (isolated large field occupies entire row)
 * "normal" = single column
 *
 * Rules:
 * - A "large" field at col 0 whose immediate next field is also "large" → both "normal" (paired in same row)
 * - Any other "large" field → "full" (forced to its own row via col-start-1 + col-span-2)
 * - All other sizes → "normal"
 *
 * @param fields - ordered list of field keys
 * @param startCol - starting column index (0 or 1), used when continuing from a previous list
 * @returns spanMap and the ending column after all fields are placed
 */
const computeFieldSpans = (
  fields: string[],
  startCol: number,
): { spanMap: Map<string, "full" | "normal">; endCol: number } => {
  const spanMap = new Map<string, "full" | "normal">();
  let col = startCol;
  let i = 0;

  while (i < fields.length) {
    const field = fields[i];
    const size = PROPERTY_FORM_FIELDS.find((f) => f.fieldKey === field)?.size;

    if (size === "large") {
      if (col === 0) {
        const nextField = fields[i + 1];
        const nextSize = PROPERTY_FORM_FIELDS.find(
          (f) => f.fieldKey === nextField,
        )?.size;
        if (nextSize === "large") {
          // Two consecutive large fields starting at col 0 → they share a row
          spanMap.set(field, "normal");
          spanMap.set(nextField, "normal");
          col = 0; // both consumed, row is full, back to col 0
          i += 2;
          continue;
        }
      }
      // Isolated large (col 1, or col 0 with no large neighbor): force to own full row
      spanMap.set(field, "full");
      col = 0;
    } else {
      spanMap.set(field, "normal");
      col = (col + 1) % 2;
    }
    i++;
  }

  return { spanMap, endCol: col };
};

const PropertyInfoForm = () => {
  const currentSurvey = useSurvey();
  const addressFieldsToUse = hasAddressLine2AsCustom(
    currentSurvey as DetailedSurvey,
  )
    ? addressFields
    : addressFieldsWithAddress2;
  const toggledFields =
    currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList
      ?.filter(
        (rule) =>
          !rule.isHidden && !addressFieldsToUse.includes(rule.fieldKey || ""),
      )
      .sort((a, b) => a.order - b.order)
      .map((rule) => rule.fieldKey || rule.fieldLabel) || [];

  const unToggledFields =
    currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList
      ?.filter(
        (rule) =>
          rule.isHidden && !addressFieldsToUse.includes(rule.fieldKey || ""),
      )
      .sort((a, b) => a.fieldLabel.localeCompare(b.fieldLabel))
      .map((rule) => rule.fieldKey || rule.fieldLabel) || [];

  const [isNewField, setIsNewField] = useState(false);

  const addCustomFieldHandler = () => {
    setIsNewField(true);
  };

  const closeHandler = () => {
    setIsNewField(false);
  };

  const { spanMap: toggledSpanMap, endCol: toggledEndCol } = computeFieldSpans(
    toggledFields,
    0,
  );
  const { spanMap: unToggledSpanMap } = computeFieldSpans(
    unToggledFields,
    toggledEndCol,
  );

  const renderFieldItem = (
    field: string,
    spanMap: Map<string, "full" | "normal">,
  ) => {
    const fieldAttribute = PROPERTY_FORM_FIELDS.find(
      (f: PropertyFormFieldConfig) => f.fieldKey === field,
    );
    const rows = fieldAttribute?.size === "large" ? 7 : undefined;
    const isFull = spanMap.get(field) === "full";

    return (
      <div
        key={field}
        className={isFull ? "col-span-2 col-start-1" : undefined}
      >
        <PropertyDisplayFields
          fieldAttribute={fieldAttribute}
          field={field}
          closeHandler={closeHandler}
          rows={rows}
          key={field}
        />
      </div>
    );
  };

  return (
    <div className="contents">
      {toggledFields.map((field) => renderFieldItem(field, toggledSpanMap))}
      {unToggledFields.map((field) => renderFieldItem(field, unToggledSpanMap))}
      {isNewField ? (
        <div style={{ order: 9999 }} className="contents">
          <AddOrEditCustomFieldInput
            key="new"
            eachField={{
              id: "new",
              label: "",
              value: "",
            }}
            onClose={closeHandler}
          />
        </div>
      ) : (
        <div style={{ order: 9999 }} className="contents">
          <EmeraldButton
            icon="add"
            type={EmeraldButtonVariant.TextButton}
            size={EmeraldButtonSize.Medium}
            nativeProps={{
              title: "Add Custom Field",
              type: "button",
              "aria-label": "Add Custom Field",
            }}
            onClick={addCustomFieldHandler}
            label="Add Custom Field"
          />
        </div>
      )}
    </div>
  );
};

export default PropertyInfoForm;
